import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PublicHeader } from "@/components/PublicHeader";
import { ArrowRight, Building2, Globe2, Search, ShieldCheck } from "lucide-react";
import { EmptyState } from "@/components/common";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    eyebrow: "Market coverage",
    h1a: "Every exchange we reach,",
    h1b: "in one account.",
    sub: "Coverage is a claim you should be able to check. Here is the whole venue list — the country, the settlement currency and how many instruments we screen on each one.",
    stats: [["Exchanges", "exchanges"], ["Countries", "countries"], ["Currencies", "currencies"],
            ["Instruments screened", "instruments"]],
    byRegion: "Coverage by region",
    venues: "venues",
    countriesLc: "countries",
    search: "Search an exchange, country or code",
    all: "All regions",
    th: ["Exchange", "Country", "Currency", "Session", "Next closure", "Instruments", "Shariah compliant"],
    openNow: "Open now",
    open: "Open",
    closed: "Closed",
    lunar: "Eid closures are confirmed by the exchange nearer the date.",
    noHoliday: "None scheduled",
    shown: "venues shown",
    empty: "No venue matched that search.",
    cta: "Open your account",
    ctaSub: "One account reaches all of it, in your own currency, with a Shariah verdict on every listing.",
    screener: "Screen any of these names",
    note: "Coverage reflects the instruments we currently screen and quote. Market data depth and order types vary by venue, and some exchanges only permit delayed prices to retail clients.",
  },
  ar: {
    eyebrow: "تغطية الأسواق",
    h1a: "كل سوق نصل إليه،",
    h1b: "في حساب واحد.",
    sub: "التغطية دعوى يجب أن تستطيع التحقق منها. هذه قائمة الأسواق كاملة — البلد وعملة التسوية وعدد الأوراق التي نفحصها في كل سوق.",
    stats: [["الأسواق", "exchanges"], ["الدول", "countries"], ["العملات", "currencies"],
            ["الأوراق المفحوصة", "instruments"]],
    byRegion: "التغطية حسب المنطقة",
    venues: "سوقاً",
    countriesLc: "دولة",
    search: "ابحث عن سوق أو بلد أو رمز",
    all: "جميع المناطق",
    th: ["السوق", "البلد", "العملة", "الجلسة", "الإغلاق القادم", "الأوراق", "متوافقة مع الشريعة"],
    openNow: "مفتوحة الآن",
    open: "مفتوحة",
    closed: "مغلقة",
    lunar: "تُعلن إغلاقات العيد من السوق نفسه قرب موعدها.",
    noHoliday: "لا إغلاق مجدول",
    shown: "سوقاً معروضاً",
    empty: "لا سوق يطابق هذا البحث.",
    cta: "افتح حسابك",
    ctaSub: "حساب واحد يصل إلى كل ذلك، بعملتك، ومع حكم شرعي لكل ورقة.",
    screener: "افحص أي من هذه الأسهم",
    note: "تعبّر التغطية عن الأوراق التي نفحصها ونسعّرها حالياً. عمق بيانات السوق وأنواع الأوامر تختلف بين الأسواق، وبعضها لا يسمح لعملاء التجزئة إلا بأسعار متأخرة.",
  },
};

const REGION_AR = {
  "Americas": "الأمريكتان", "Europe": "أوروبا", "Middle East": "الشرق الأوسط",
  "Africa": "أفريقيا", "Asia-Pacific": "آسيا والمحيط الهادئ",
};

const COUNTRY_AR = {
  "United States": "الولايات المتحدة", "Canada": "كندا", "Brazil": "البرازيل", "Mexico": "المكسيك",
  "Argentina": "الأرجنتين", "Chile": "تشيلي", "Peru": "بيرو",
  "United Kingdom": "المملكة المتحدة", "Germany": "ألمانيا", "France": "فرنسا",
  "Netherlands": "هولندا", "Belgium": "بلجيكا", "Portugal": "البرتغال", "Ireland": "أيرلندا",
  "Italy": "إيطاليا", "Spain": "إسبانيا", "Switzerland": "سويسرا", "Austria": "النمسا",
  "Sweden": "السويد", "Denmark": "الدنمارك", "Finland": "فنلندا", "Norway": "النرويج",
  "Greece": "اليونان", "Poland": "بولندا", "Czechia": "التشيك", "Hungary": "هنغاريا",
  "Romania": "رومانيا", "Bulgaria": "بلغاريا", "Türkiye": "تركيا",
  "Saudi Arabia": "السعودية", "United Arab Emirates": "الإمارات", "Qatar": "قطر",
  "Kuwait": "الكويت", "Bahrain": "البحرين", "Jordan": "الأردن",
  "Egypt": "مصر", "Morocco": "المغرب", "South Africa": "جنوب أفريقيا", "Nigeria": "نيجيريا",
  "Japan": "اليابان", "Hong Kong": "هونغ كونغ", "China": "الصين", "South Korea": "كوريا الجنوبية",
  "Taiwan": "تايوان", "India": "الهند", "Singapore": "سنغافورة", "Malaysia": "ماليزيا",
  "Indonesia": "إندونيسيا", "Thailand": "تايلاند", "Philippines": "الفلبين", "Vietnam": "فيتنام",
  "Australia": "أستراليا", "Pakistan": "باكستان", "Bangladesh": "بنغلاديش",
  "Sri Lanka": "سريلانكا",
};

export default function Exchanges() {
  const navigate = useNavigate();
  const { lang } = useApp();
  const ar = lang === "ar";
  const c = COPY[ar ? "ar" : "en"];
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("All");
  const [openOnly, setOpenOnly] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/exchanges`).then((r) => setD(r.data)).catch(() => {});
  }, []);

  const items = useMemo(() => {
    if (!d) return [];
    const needle = q.trim().toLowerCase();
    return d.items.filter((r) =>
      (region === "All" || r.region === region) &&
      (!openOnly || r.session.is_open) &&
      (!needle || r.exchange.toLowerCase().includes(needle) ||
        r.country.toLowerCase().includes(needle) || r.code.toLowerCase().includes(needle)));
  }, [d, q, region, openOnly]);

  const cname = (v) => (ar ? COUNTRY_AR[v] || v : v);
  const rname = (v) => (ar ? REGION_AR[v] || v : v);

  return (
    <div className="min-h-screen bg-background" data-testid="exchanges-page">
      <PublicHeader />

      <div className="navy-strip text-white relative overflow-hidden">
        <div className="absolute inset-0 hero-sheen opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[color:var(--tharwy-lime)]">
            <Globe2 className="h-3.5 w-3.5" />{c.eyebrow}
          </div>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            data-testid="exchanges-title">
            {c.h1a}<br /><span className="text-white/70">{c.h1b}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/70 leading-relaxed">{c.sub}</p>

          {d && (
            <div className="mt-9 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="exchanges-stats">
              {c.stats.map(([label, key]) => (
                <div key={key} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-white/45">{label}</div>
                  <div className="mt-1 numeric text-2xl font-bold">
                    {d.totals[key].toLocaleString(ar ? "ar-QA" : undefined)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16 space-y-12">
        {/* coverage by region */}
        {d && (
          <section data-testid="exchanges-by-region">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{c.byRegion}</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {d.by_region.map((r) => (
                <Card key={r.region} className="p-5 shadow-premium"
                  data-testid={`region-card-${r.region.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Globe2 className="h-4 w-4 text-[#3E2E65]" />{rname(r.region)}
                  </div>
                  <div className="mt-3 numeric text-2xl font-bold">{r.venues}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {c.venues} · {r.countries} {c.countriesLc}
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${r.compliant_pct}%` }} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="numeric">{r.instruments.toLocaleString()}</span>
                    <span className="numeric text-emerald-700">{r.compliant_pct}%</span>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* filters */}
        <section>
          <div className="flex flex-wrap items-center gap-2.5" data-testid="exchanges-filters">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={c.search}
                className="ps-9 h-10" data-testid="exchanges-search" />
            </div>
            <button onClick={() => setOpenOnly(!openOnly)}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                openOnly ? "bg-emerald-600 text-white border-emerald-600"
                         : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid="filter-open-now">
              <span className={`h-1.5 w-1.5 rounded-full ${openOnly ? "bg-white" : "bg-emerald-500"}`} />
              {c.openNow}{d ? ` (${d.totals.open_now})` : ""}
            </button>
            <div className="flex flex-wrap gap-2">
              {["All", ...(d?.regions || [])].map((r) => (
                <button key={r} onClick={() => setRegion(r)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                    region === r ? "bg-[#3E2E65] text-white border-[#3E2E65]"
                                 : "border-border text-muted-foreground hover:text-foreground"}`}
                  data-testid={`region-${r.replace(/\s+/g, "-").toLowerCase()}`}>
                  {r === "All" ? c.all : rname(r)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid="exchanges-count">
            <Building2 className="h-3.5 w-3.5" />{items.length} {c.shown}
          </div>

          <div className="mt-4 rounded-2xl border border-border overflow-hidden bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1040px]" data-testid="exchanges-table">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    {c.th.map((h, i) => (
                      <th key={h} className={`font-semibold px-4 py-3 ${i > 4 ? "text-end" : "text-start"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((r) => (
                    <tr key={r.code} className="hover:bg-muted/40 transition-colors"
                      data-testid={`exchange-row-${r.code}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{r.exchange}</div>
                        <div className="text-[11px] text-muted-foreground">{r.code} · {rname(r.region)}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{cname(r.country)}</td>
                      <td className="px-4 py-3 numeric">{r.currency}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                          r.session.is_open ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : "bg-secondary text-muted-foreground border-border"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${r.session.is_open ? "bg-emerald-500" : "bg-muted-foreground/50"}`} />
                          {r.session.is_open ? c.open : c.closed}
                        </span>
                        <div className="mt-1 text-[10px] text-muted-foreground numeric">
                          {r.session.hours} · {r.session.tz_label}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {r.next_holiday ? (
                          <>
                            <div className="text-xs font-medium">{r.next_holiday.name}</div>
                            <div className="text-[10px] text-muted-foreground numeric">
                              {r.next_holiday.date} · {r.next_holiday.in_days}d
                            </div>
                          </>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">{c.noHoliday}</span>
                        )}
                        {r.lunar_holidays && (
                          <div className="text-[10px] text-muted-foreground/80 mt-0.5">+ Eid</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-end numeric">{r.instruments}</td>
                      <td className="px-4 py-3 text-end">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          <ShieldCheck className="h-3 w-3" />{r.compliant} · {r.compliant_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d && items.length === 0 && (
              <div className="p-8 flex justify-center" data-testid="exchanges-empty">
                <EmptyState title={c.empty} />
              </div>
            )}
          </div>
          <p className="mt-5 text-xs text-muted-foreground leading-relaxed">{c.lunar} {c.note}</p>
        </section>

        <section className="rounded-3xl navy-strip text-white p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 hero-sheen opacity-50" />
          <div className="relative">
            <p className="text-white/70 max-w-xl mx-auto">{c.ctaSub}</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Button className="rounded-full px-8 h-11" onClick={() => navigate("/login")}
                data-testid="exchanges-cta">{c.cta} <ArrowRight className="h-4 w-4 ms-1.5" /></Button>
              <Button variant="outline" onClick={() => navigate("/top-halal-stocks")}
                className="rounded-full px-8 h-11 bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
                data-testid="exchanges-screener-cta">{c.screener}</Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
